import { CSSProperties, useRef, useState } from 'react';

import { ScrollEventWrapper } from '@/components/common/ScrollEventWrapper';
import { useSectionScrollModel } from '@/components/common/context/SectionScrollModelProvider';
import { VIDEO_TYPE } from '@/lib/constants/event';

export const StaticImageSection = ({
  sectionId,
  imagePath,
  imageAlt,
  className,
  threshold,
  style,
  maxWidth = '900'
}: {
  sectionId: string;
  imagePath: string;
  imageAlt?: string;
  className?: string;
  threshold?: number;
  style?: CSSProperties;
  maxWidth?: '700' | '900';
}) => {
  const alt = imageAlt ?? imagePath.toLowerCase().match(/([^/]+)(?=\.\w+$)/)?.[0] ?? 'image';
  const maxWidthClass = maxWidth === '900' ? 'max-w-[900px]' : 'max-w-[700px]';
  return (
    <ScrollEventWrapper sectionId={sectionId} threshold={threshold}>
      <div style={style} className={`flex justify-center ${className}`}>
        <div className={`flex items-center w-full ${maxWidthClass}`}>
          <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}${imagePath}`} className={'w-full'} alt={alt} loading={'lazy'} />
        </div>
      </div>
    </ScrollEventWrapper>
  );
};

export const ResponsiveImageSection = ({
  sectionId,
  imagePaths,
  className,
  thresholds,
  maxWidth = '900'
}: {
  sectionId: string;
  imagePaths: { mobile: string; pc: string };
  className?: string;
  thresholds?: { mobile: number; pc: number };
  maxWidth?: '700' | '900';
}) => {
  const { screenType } = useSectionScrollModel();
  const imagePath = imagePaths[screenType];
  const imageAlt = (imagePath.toLowerCase().match(/([^/]+)(?=\.\w+$)/)?.[0] ?? 'image').replace(/-pc|-mobile/, '');
  const threshold = thresholds?.[screenType] ?? (screenType === 'mobile' ? 0.5 : 0.75);

  return <StaticImageSection sectionId={sectionId} imagePath={imagePath} imageAlt={imageAlt} className={className} maxWidth={maxWidth} threshold={threshold} />;
};

export const VideoSection = ({
  sectionId,
  imagePaths,
  videoPath,
  posterPath,
  className,
  threshold,
  maxWidth = '900'
}: {
  sectionId: string;
  imagePaths?: { top?: string; bottom?: string };
  videoPath: string;
  posterPath: string;
  className?: string;
  threshold?: number;
  maxWidth?: '700' | '900';
}) => {
  const [isVideoLoadFailed, setIsVideoLoadFailed] = useState(false);
  const [isTopImageLoaded, setIsTopImageLoaded] = useState(!imagePaths?.top);
  const videoRef = useRef<HTMLVideoElement>(null);
  const extension = videoPath.split('.').pop()?.toLowerCase() ?? '';
  const topImageAlt = imagePaths?.top?.toLowerCase().match(/([^/]+)(?=\.\w+$)/)?.[0] ?? '';
  const bottomImageAlt = imagePaths?.bottom?.toLowerCase().match(/([^/]+)(?=\.\w+$)/)?.[0] ?? '';
  const maxWidthClass = maxWidth === '900' ? 'max-w-[900px]' : 'max-w-[700px]';

  return (
    <ScrollEventWrapper sectionId={sectionId} threshold={threshold}>
      <div className={`flex justify-center ${className}`}>
        <div className={`w-full ${maxWidthClass} min-h-40`}>
          {imagePaths?.top && (
            <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}${imagePaths.top}`} alt={topImageAlt} onLoad={() => setIsTopImageLoaded(true)} />
          )}
          <div className={'px-5 py-5 lg:px-10 min-h-24'}>
            {(isTopImageLoaded && !isVideoLoadFailed && (
              <video
                ref={videoRef}
                className={'w-full rounded-2.5 lg:rounded-[40px]'}
                autoPlay
                loop
                playsInline
                muted
                poster={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}${posterPath}`}
                onError={(error) => {
                  console.warn(error);
                  setIsVideoLoadFailed(true);
                }}
              >
                <source src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}${videoPath}`} type={VIDEO_TYPE[extension] ?? 'video/mp4'} />
              </video>
            )) || (
              <img
                src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}${posterPath}`}
                className={'w-full rounded-5 lg:rounded-[40px]'}
                alt={'video thumbnail'}
              />
            )}
          </div>
          {imagePaths?.bottom && <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}${imagePaths.bottom}`} alt={bottomImageAlt} />}
        </div>
      </div>
    </ScrollEventWrapper>
  );
};
